import React from "react";
import { DataGrid, GridColDef, GridRowParams, GridSortModel, GridToolbar } from "@mui/x-data-grid";
import { ApiResponse } from "../../../services/api";
import { MinhaCopiaResumo } from "../../../types/minhaCopia";

interface MinhasCopiasTableProps {
  rows: MinhaCopiaResumo[];
  columns: GridColDef[];
  apiResponse: ApiResponse<MinhaCopiaResumo> | null;
  paginationModel: any;
  setPaginationModel: any;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  onRowClick: (row: MinhaCopiaResumo) => void;
}

/**
 * Grid do módulo "Minhas Cópias" - cópia de EmailLogTable (mesmo comportamento e
 * estilo visual), com três diferenças:
 * - Colunas autodimensionadas pelo conteúdo (autosizeOnMount), não largura fixa.
 * - Toolbar padrão do MUI (GridToolbar) ligada: dá filtro por coluna, escolha de
 *   colunas visíveis e busca rápida, de graça.
 * - Ordenação por coluna é server-side (sortingMode="server") - com paginação
 *   server-side, ordenar só a página carregada seria enganoso (mesmo motivo que
 *   EmailLogTable evita ordenação: aqui, em vez de evitar, resolvemos no backend).
 *
 * O filtro do toolbar, esse sim, é só na página carregada (client-side) - filtrar de
 * verdade em todas as páginas exigiria mandar cada operador de filtro pro backend, que
 * não fizemos aqui.
 *
 * Cada linha é clicável e leva ao detalhe (ver ViewById.tsx), onde aparece o status do
 * e-mail vinculado, quando houver.
 */
const MinhasCopiasTable: React.FC<MinhasCopiasTableProps> = ({
  rows,
  columns,
  apiResponse,
  paginationModel,
  setPaginationModel,
  sortModel,
  onSortModelChange,
  onRowClick,
}) => {
  return (
    <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          rowHeight={36}
          columnHeaderHeight={40}
          pageSizeOptions={[10, 20, 40, 100]}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={apiResponse?.data?.totalItems ?? 0}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          autosizeOnMount
          autosizeOptions={{ includeHeaders: true, includeOutliers: true, expand: true }}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true } }}
          checkboxSelection={false}
          disableRowSelectionOnClick
          onRowClick={(params: GridRowParams) => onRowClick(params.row as MinhaCopiaResumo)}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-row:nth-of-type(even)': {
              backgroundColor: '#f5f5f5',
            },
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: 'white',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#b2d1ff',
            },
            '& .MuiTablePagination-selectLabel': {
              marginTop: '14px',
            },
            '& .MuiTablePagination-displayedRows': {
              marginTop: '14px',
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:focus, & .MuiDataGrid-row:focus-within': {
              outline: 'none',
            },
          }}
        />
      </div>
    </div>
  );
};

export default MinhasCopiasTable;
